import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';
import { getCategoriesNameById } from '../utils/helpers';

// Helper function to get room name
const getRoomName = (roomId, roomsData) => {
    if (!roomsData || !Array.isArray(roomsData)) return 'Unknown';
    const room = roomsData.find(r => parseInt(r.id) === parseInt(roomId));
    return room ? room.roomtype : 'Unknown';
};

// Helper function to get booking status text
const getBookingStatusText = (status) => {
    const statusMap = {
        0: 'Cancelled',
        1: 'Active',
        2: 'Checked In',
        3: 'Checked Out'
    };
    return statusMap[status] || 'Unknown';
};

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Manual table drawing function for PDF
const drawRoomStatusTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const rowHeight = 12;

    // Column widths
    const colWidths = [10, 35, 20, 25, 20, 25, 30, 20, 25]; // #, Room, Status, Check In, Check Out, Customer, Amount, Nights, Branch

    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;
    if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
        doc.addPage();
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');

    const headers = ['#', 'Room', 'Status', 'Check In', 'Check Out', 'Customer', 'Amount', 'Nights', 'Branch'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);

    data.forEach((row, rowIndex) => {
        // Calculate needed height for this row
        let maxLines = 1;
        const cellLines = row.map((cell, cellIndex) => {
            const text = String(cell);
            const maxWidth = colWidths[cellIndex] - 4;
            const lines = splitTextIntoLines(doc, text, maxWidth);
            maxLines = Math.max(maxLines, lines.length);
            return lines;
        });

        const actualRowHeight = rowHeight * maxLines;

        // Check for page break
        if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
            doc.addPage();
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(255, 255, 255);
            doc.setTextColor(25, 118, 210);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            doc.setDrawColor(25, 118, 210);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
        
        // Draw row text with multi-line support
        cellLines.forEach((lines, cellIndex) => {
            xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
            
            lines.forEach((line, lineIndex) => {
                const lineY = currentY + 7 + (lineIndex * rowHeight);
                doc.text(line, xPos + 2, lineY);
            });
        });
        
        currentY += actualRowHeight;
    });

    return currentY;
};

// Calculate nights between dates
const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
};

// Check room availability
const checkRoomAvailability = (roomId, bookings, checkIn, checkOut) => {
    const roomBookings = bookings.filter(booking => 
        parseInt(booking.room_type) === parseInt(roomId) &&
        booking.status !== 0 && // Not cancelled
        booking.status !== 3    // Not checked out
    );

    if (roomBookings.length === 0) return true;

    const requestedCheckIn = new Date(checkIn);
    const requestedCheckOut = new Date(checkOut);

    for (const booking of roomBookings) {
        const bookingCheckIn = new Date(booking.check_in);
        const bookingCheckOut = new Date(booking.check_out);

        // Check for overlapping dates
        if (
            (requestedCheckIn >= bookingCheckIn && requestedCheckIn < bookingCheckOut) ||
            (requestedCheckOut > bookingCheckIn && requestedCheckOut <= bookingCheckOut) ||
            (requestedCheckIn <= bookingCheckIn && requestedCheckOut >= bookingCheckOut)
        ) {
            return false; // Room is booked
        }
    }

    return true; // Room is available
};

// Get room statistics
const getRoomStatistics = (rooms, bookings, filters) => {
    const stats = {
        total_rooms: rooms.length,
        available_rooms: 0,
        booked_rooms: 0,
        checked_in_rooms: 0,
        maintenance_rooms: 0,
        occupancy_rate: 0,
        revenue_potential: 0,
        average_rate: 0
    };

    // Calculate room status counts
    rooms.forEach(room => {
        const roomBookings = bookings.filter(booking => 
            parseInt(booking.room_type) === parseInt(room.id)
        );

        const activeBooking = roomBookings.find(booking => 
            booking.status === 1 || booking.status === 2
        );

        if (activeBooking) {
            if (activeBooking.status === 2) {
                stats.checked_in_rooms++;
            }
            stats.booked_rooms++;
        } else {
            stats.available_rooms++;
        }

        // Check if room is under maintenance (you might have a separate flag)
        if (room.status === 0) {
            stats.maintenance_rooms++;
        }

        stats.revenue_potential += parseFloat(room.defaultrate) || 0;
    });

    stats.occupancy_rate = stats.total_rooms > 0 
        ? Math.round((stats.booked_rooms / stats.total_rooms) * 100)
        : 0;
    
    stats.average_rate = stats.total_rooms > 0
        ? Math.round(stats.revenue_potential / stats.total_rooms)
        : 0;

    return stats;
};

// Client-side Excel export for Room Status
export const exportRoomStatusToExcel = (rooms, bookings, categoriesData = [], filters = {}) => {
    try {
        // Prepare data for Excel
        const excelData = rooms.map((room, index) => {
            const roomBookings = bookings.filter(booking => 
                parseInt(booking.room_type) === parseInt(room.id)
            );

            const activeBooking = roomBookings.find(booking => 
                booking.status === 1 || booking.status === 2
            );

            return {
                'No.': index + 1,
                'Room ID': room.id,
                'Room Name': room.roomtype,
                'Status': activeBooking ? getBookingStatusText(activeBooking.status) : 'Available',
                'Capacity': room.capacity,
                'Default Rate': formatCurrency(room.defaultrate || 0),
                'Bed Charge': formatCurrency(room.bedcharge || 0),
                'Person Charge': formatCurrency(room.personcharge || 0),
                'Total Charge': formatCurrency(room.defaultrate + room.bedcharge + room.personcharge),
                'Size': `${room.roomsize} ${room.size_unit || 'sq ft'}`,
                'Beds': room.bedsno,
                'Bed Type': getCategoriesNameById(room.bedstype, categoriesData),
                'Current Customer': activeBooking ? activeBooking.customer_name : 'N/A',
                'Check In': activeBooking ? formatDate(activeBooking.check_in) : 'N/A',
                'Check Out': activeBooking ? formatDate(activeBooking.check_out) : 'N/A',
                'Nights': activeBooking ? calculateNights(activeBooking.check_in, activeBooking.check_out) : 0,
                'Amount': activeBooking ? formatCurrency(activeBooking.total_amount || 0) : 'N/A',
                'Room Status': room.status === 1 ? 'Active' : 'Inactive'
            };
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Room Status');

        // Generate Excel file and download
        const fileName = `room_status_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Room Status Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// PDF export for Room Status
export const exportRoomStatusToPDF = (rooms, bookings, categoriesData = [], filters = {}) => {
    try {
        const doc = new jsPDF();
        
        // Set document properties
        doc.setProperties({
            title: 'Room Status Report',
            subject: 'Room Status Export',
            author: companyInfo.name,
            keywords: 'rooms, status, availability, booking, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('ROOM STATUS REPORT', 105, 25, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 35;
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
        currentY += 6;
        
        // Get statistics
        const stats = getRoomStatistics(rooms, bookings, filters);
        doc.text(`Total Rooms: ${stats.total_rooms} | Available: ${stats.available_rooms} | Booked: ${stats.booked_rooms} | Occupancy: ${stats.occupancy_rate}%`, 14, currentY);
        currentY += 12;
        
        // Filters applied
        if (filters.dateFrom || filters.dateTo) {
            doc.text('Applied Filters:', 14, currentY);
            currentY += 6;
            
            if (filters.dateFrom) {
                doc.text(`• From date: ${formatDate(filters.dateFrom)}`, 20, currentY);
                currentY += 6;
            }
            if (filters.dateTo) {
                doc.text(`• To date: ${formatDate(filters.dateTo)}`, 20, currentY);
                currentY += 6;
            }
            currentY += 6;
        }
        
        // Prepare detailed table data
        const tableData = rooms.map((room, index) => {
            const roomBookings = bookings.filter(booking => 
                parseInt(booking.room_type) === parseInt(room.id)
            );

            const activeBooking = roomBookings.find(booking => 
                booking.status === 1 || booking.status === 2
            );

            return [
                (index + 1).toString(),
                room.roomtype,
                activeBooking ? getBookingStatusText(activeBooking.status) : 'Available',
                activeBooking ? formatDate(activeBooking.check_in) : 'N/A',
                activeBooking ? formatDate(activeBooking.check_out) : 'N/A',
                activeBooking ? (activeBooking.customer_name || 'N/A').substring(0, 15) : 'Available',
                formatCurrency(activeBooking ? (activeBooking.total_amount || 0) : (room.defaultrate + room.bedcharge + room.personcharge)),
                activeBooking ? calculateNights(activeBooking.check_in, activeBooking.check_out).toString() : '0',
                room.status === 1 ? 'Active' : 'Inactive'
            ];
        });
        
        // Draw the table
        const finalY = drawRoomStatusTable(doc, tableData, currentY);
        
        // Add summary at the end
        doc.setFontSize(11);
        doc.setTextColor(25, 118, 210);
        doc.text('ROOM STATUS SUMMARY', 14, finalY + 15);
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        
        doc.text(`• Total Rooms: ${stats.total_rooms}`, 14, finalY + 25);
        doc.text(`• Available Rooms: ${stats.available_rooms} (${Math.round((stats.available_rooms / stats.total_rooms) * 100)}%)`, 14, finalY + 35);
        doc.text(`• Booked Rooms: ${stats.booked_rooms} (${stats.occupancy_rate}% occupancy)`, 14, finalY + 45);
        doc.text(`• Checked In: ${stats.checked_in_rooms}`, 14, finalY + 55);
        doc.text(`• Under Maintenance: ${stats.maintenance_rooms}`, 14, finalY + 65);
        doc.text(`• Total Revenue Potential: ${formatCurrency(stats.revenue_potential)}`, 14, finalY + 75);
        doc.text(`• Average Room Rate: ${formatCurrency(stats.average_rate)}`, 14, finalY + 85);
        
        // Add legend
        doc.setFontSize(10);
        doc.setTextColor(25, 118, 210);
        doc.text('LEGEND:', 14, finalY + 100);
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text('• Available: Room is vacant and ready for booking', 20, finalY + 110);
        doc.text('• Active: Room is booked but guest hasn\'t checked in yet', 20, finalY + 120);
        doc.text('• Checked In: Guest is currently occupying the room', 20, finalY + 130);
        doc.text('• Checked Out: Guest has departed, room needs cleaning', 20, finalY + 140);
        
        const fileName = `room_status_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Room Status PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// Simple CSV export for Room Status
export const exportRoomStatusToCSV = (rooms, bookings) => {
    try {
        const headers = ['Room ID', 'Room Name', 'Status', 'Capacity', 'Default Rate', 'Bed Charge', 'Person Charge', 'Size', 'Beds', 'Current Customer', 'Check In', 'Check Out', 'Nights', 'Amount'];
        
        const csvData = rooms.map(room => {
            const roomBookings = bookings.filter(booking => 
                parseInt(booking.room_type) === parseInt(room.id)
            );

            const activeBooking = roomBookings.find(booking => 
                booking.status === 1 || booking.status === 2
            );

            return [
                room.id,
                `"${room.roomtype.replace(/"/g, '""')}"`,
                activeBooking ? getBookingStatusText(activeBooking.status) : 'Available',
                room.capacity,
                room.defaultrate,
                room.bedcharge,
                room.personcharge,
                `${room.roomsize} ${room.size_unit || 'sq ft'}`,
                room.bedsno,
                activeBooking ? `"${(activeBooking.customer_name || 'N/A').replace(/"/g, '""')}"` : 'N/A',
                activeBooking ? `"${formatDate(activeBooking.check_in)}"` : 'N/A',
                activeBooking ? `"${formatDate(activeBooking.check_out)}"` : 'N/A',
                activeBooking ? calculateNights(activeBooking.check_in, activeBooking.check_out) : 0,
                activeBooking ? (activeBooking.total_amount || 0) : (room.defaultrate + room.bedcharge + room.personcharge)
            ];
        });

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `room_status_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Room Status CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};

export {
    checkRoomAvailability,
    getRoomStatistics,
    getRoomName,
    getBookingStatusText,
    formatDate,
    calculateNights
};